import React, { useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import './Modal.css'

export interface ModalProps {
  /** Is modal open */
  isOpen: boolean
  /** Close handler */
  onClose: () => void
  /** Modal title */
  title?: string
  /** Modal content */
  children: React.ReactNode
  /** Footer content (buttons) */
  footer?: React.ReactNode
  /** Modal size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  /** Close on overlay click */
  closeOnOverlayClick?: boolean
  /** Close on ESC key */
  closeOnEsc?: boolean
  /** Show close button in header */
  showCloseButton?: boolean
  /** Additional CSS class */
  className?: string
  /** Prevent body scroll when open */
  preventBodyScroll?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  className = '',
  preventBodyScroll = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Handle ESC key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && closeOnEsc) {
      onClose()
    }
  }, [closeOnEsc, onClose])

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose()
    }
  }

  // Setup effects
  useEffect(() => {
    if (isOpen) {
      // Store current focus
      previousActiveElement.current = document.activeElement as HTMLElement

      // Add event listeners
      document.addEventListener('keydown', handleKeyDown)

      // Prevent body scroll
      if (preventBodyScroll) {
        document.body.style.overflow = 'hidden'
      }

      // Focus modal
      setTimeout(() => {
        modalRef.current?.focus()
      }, 0)

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        
        if (preventBodyScroll) {
          document.body.style.overflow = ''
        }

        // Restore focus
        previousActiveElement.current?.focus()
      }
    }
  }, [isOpen, handleKeyDown, preventBodyScroll])

  if (!isOpen) {
    return null
  }

  const modalContent = (
    <div 
      className="modal-overlay" 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div 
        ref={modalRef}
        className={`modal modal-${size} ${className}`}
        tabIndex={-1}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && (
              <h2 id="modal-title" className="modal-title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className="modal-close"
                onClick={onClose}
                aria-label="Schließen"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  )

  // Render in portal
  return createPortal(modalContent, document.body)
}

export default Modal
